import {
  QueryClient,
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Item, SnapshotImage, Video, VideoStatus } from 'src/types';
import { preserveAwsUrl } from 'src/util/preserveAwsUrl';
import { createScopedMutation } from '../createScopedMutation';
import { postImage } from '../Image';
import {
  JOB_QUERY_KEY,
  ENTITY_KEY,
  IMAGE_QUERY_KEY,
  ITEM_QUERY_KEY,
  LIST_KEY,
  POST_KEY,
  PUT_KEY,
  ROOM_QUERY_KEY,
  VIDEO_QUERY_KEY,
} from '../queryKeys';
import {
  invalidateIfAllMutationsDone,
  JSON_MUTATION_TIMEOUT_MS,
  queryYemboApiCall,
  queryYemboApiCallWithReturnValue,
} from '../queryYemboApiCalls';
import { useIsReadyForFetch } from '../useIsReadyForFetch';

const url = 'video';

/** A clip is tens of megabytes and is stored before the response returns. */
const VIDEO_UPLOAD_TIMEOUT_MS = 10 * 60 * 1000;

/**
 * Statuses that mean `service-voyager-video` has not finished with the clip.
 * `uploaded` is what `POST /video` stamps on a freshly uploaded file, so a
 * back-office upload has to be polled the same way a field recording is.
 */
const PENDING_VIDEO_STATUSES: VideoStatus[] = ['new', 'uploaded', 'processing'];

type PartialVideo = Partial<Video> & Pick<Video, 'key'>;

/** `GET /video?jobKey=` answers `{ videos }`: the job's videos, by each video's own jobKey. */
const getVideos = async (jobKey: string): Promise<Video[]> => {
  const videos = await queryYemboApiCallWithReturnValue<Video[], 'videos'>({
    url,
    key: 'videos',
    urlParams: new URLSearchParams({ jobKey }),
    shouldNotifyAndThrowError: true,
  });

  if (!videos) throw new Error('Job not found');

  return videos;
};

const putVideo = async (video: PartialVideo) =>
  queryYemboApiCall({
    url,
    method: 'PUT',

    body: JSON.stringify(video),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

/**
 * Uploads a clip file straight to `POST /video`, which stores it in S3, creates
 * the Video and enqueues ProcessVideo so `service-voyager-video` detects the
 * items in it. The room travels in `X-Room-Key` and the file in the multipart
 * `file` field — the shape the iPad capture app established.
 *
 * This is the back-office equivalent of recording a clip in the field: the
 * adjuster or vendor picks a video file they already have.
 */
export const postVideoUpload = async ({ roomKey, file }: { roomKey: string; file: File }): Promise<Video> => {
  const form = new FormData();
  form.append('file', file, file.name);

  // 201 with the video itself; an X-Room-Key that names no room is a 400.
  const video = await queryYemboApiCallWithReturnValue<Video>({
    url,
    method: 'POST',
    body: form,
    headers: { 'X-Room-Key': roomKey },
    shouldNotifyAndThrowError: true,
    errorOnNotFound: true,
    timeoutMs: VIDEO_UPLOAD_TIMEOUT_MS,
  });

  if (!video) throw new Error('The clip could not be uploaded');

  return video;
};

export const postVideoUploadRequest = async () => {
  const result = await queryYemboApiCallWithReturnValue<Record<string, string>, 'formInputs'>({
    url: `${url}/upload-request`,
    method: 'POST',
    key: 'formInputs',
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

  if (!result) throw new Error('Video upload request failed');

  return result;
};

const postVideo = (video: Video, skipTranscoding?: boolean, skipAnalysis?: boolean) =>
  queryYemboApiCall({
    url,
    method: 'POST',
    body: JSON.stringify({
      ...video,
      skipTranscoding,
      skipAnalysis,
    }),
    shouldNotifyAndThrowError: true,
    timeoutMs: JSON_MUTATION_TIMEOUT_MS,
  });

const preserveAwsUrls = (
  prevVideo: Pick<Video, 'url' | 'importedUrl'> | undefined,
  newVideo: Pick<Video, 'url' | 'importedUrl'>
) => ({
  url: preserveAwsUrl(prevVideo?.url, newVideo.url),
  importedUrl: preserveAwsUrl(prevVideo?.importedUrl, newVideo.importedUrl),
});

type UseVideosOptions = Partial<UseQueryOptions<Video[], unknown>>;

const useVideosQuery = (jobKey: string | undefined, options?: UseVideosOptions) => {
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: [VIDEO_QUERY_KEY, LIST_KEY, jobKey],
    queryFn: async () => {
      let videos = await getVideos(jobKey ?? '');
      const prevVideos = queryClient.getQueryData<Video[]>([VIDEO_QUERY_KEY, LIST_KEY, jobKey]);

      if (!prevVideos) return videos;

      // Use previous videos AWS signed URLs to avoid re-uploading them that causes various issues
      // like blinking and video player position reset
      videos = videos.map((newVideo) => ({
        ...newVideo,
        ...preserveAwsUrls(
          prevVideos?.find(({ key }) => key === newVideo.key),
          newVideo
        ),
      }));

      for (const video of videos) {
        const queryKey = [VIDEO_QUERY_KEY, ENTITY_KEY, video.key];
        await queryClient.cancelQueries({ queryKey });
        queryClient.setQueryData<Video>(queryKey, video);
      }

      return videos;
    },
    ...options,
    enabled: !!jobKey && options?.enabled !== false,
  });
};

export const useVideosQueryForOffline = (jobKey: string | undefined, options?: UseVideosOptions) =>
  useVideosQuery(jobKey, {
    ...options,
    enabled: useIsReadyForFetch() && options?.enabled !== false,
  });

/** `GET /video/:key` answers the video itself. */
const getVideo = async (key: string): Promise<Video> => {
  const video = await queryYemboApiCallWithReturnValue<Video>({
    url: `${url}/${key}`,
    shouldNotifyAndThrowError: true,
  });

  if (!video) throw new Error('Video not found');

  return video;
};

export const useSyncNewVideoQueries = (videos: Video[], jobKey: string) => {
  const queryClient = useQueryClient();

  useQueries({
    queries: videos.map(({ key, status }): UseQueryOptions<Video, unknown, unknown> => {
      const isNew = PENDING_VIDEO_STATUSES.includes(status);
      return {
        queryKey: [VIDEO_QUERY_KEY, ENTITY_KEY, key],
        queryFn: async () => {
          const video = await getVideo(key);

          const listQueryKey = [VIDEO_QUERY_KEY, LIST_KEY, jobKey];

          await queryClient.cancelQueries({ queryKey: listQueryKey });

          queryClient.setQueryData<Video[]>(listQueryKey, (prevVideos) =>
            prevVideos?.map((prevVideo) => (prevVideo.key === key ? video : prevVideo))
          );

          if (!PENDING_VIDEO_STATUSES.includes(video.status)) {
            // Invalidate job, rooms, videos, images and items queries
            // to get updated properties and data from the newly processed video
            queryClient.invalidateQueries({
              queryKey: [JOB_QUERY_KEY, ENTITY_KEY, jobKey],
            });
            queryClient.invalidateQueries({
              queryKey: [ROOM_QUERY_KEY, LIST_KEY, jobKey],
            });
            queryClient.invalidateQueries({
              queryKey: [VIDEO_QUERY_KEY, LIST_KEY, jobKey],
            });
            queryClient.invalidateQueries({
              queryKey: [IMAGE_QUERY_KEY, LIST_KEY, jobKey],
            });
            queryClient.invalidateQueries({
              queryKey: [ITEM_QUERY_KEY, LIST_KEY, jobKey],
            });
          }

          return video;
        },
        // Fetch with the interval only new videos
        refetchInterval: isNew ? 10_000 : false,
        enabled: isNew,
      };
    }),
  });
};

type PutVideoOptions = UseMutationOptions<Response, unknown, PartialVideo>;

export const usePutVideo = (roomKey: string, jobKey: string, options?: PutVideoOptions) => {
  const queryClient = useQueryClient();

  const listQueryKey = [VIDEO_QUERY_KEY, LIST_KEY, jobKey];
  const mutationKey = [VIDEO_QUERY_KEY, jobKey, PUT_KEY];

  return useMutation({
    mutationKey,
    scope: { id: roomKey },
    mutationFn: putVideo,
    ...options,
    onMutate: async (video, context) => {
      await queryClient.cancelQueries({ queryKey: listQueryKey });

      queryClient.setQueryData<Video[]>(listQueryKey, (prevVideos) =>
        prevVideos?.map((prevVideo) => (prevVideo.key === video.key ? { ...prevVideo, ...video } : prevVideo))
      );

      options?.onMutate?.(video, context);
    },
    onSettled: (response, error, video, onMutateResult, context) => {
      invalidateIfAllMutationsDone(listQueryKey, [VIDEO_QUERY_KEY, jobKey]);
      options?.onSettled?.(response, error, video, onMutateResult, context);
    },
  });
};

type PostVideoOptions = UseMutationOptions<
  void,
  unknown,
  {
    video: Video;
    skipTranscoding?: boolean;
    skipAnalysis?: boolean;
    initialImage?: SnapshotImage;
  },
  void
>;

const getPostVideoOptions = (
  queryClient: QueryClient,
  roomKey: string,
  jobKey: string,
  options?: PostVideoOptions
): PostVideoOptions => {
  const mutationKey = [VIDEO_QUERY_KEY, jobKey, POST_KEY];
  const listQueryKey = [VIDEO_QUERY_KEY, LIST_KEY, jobKey];
  const imageListQueryKey = [IMAGE_QUERY_KEY, LIST_KEY, jobKey];
  const tagListQueryKey = [ITEM_QUERY_KEY, LIST_KEY, jobKey];

  return {
    mutationKey,
    scope: { id: roomKey },
    mutationFn: async ({ video, skipTranscoding, skipAnalysis, initialImage }) => {
      await postVideo(video, skipTranscoding, skipAnalysis);

      if (initialImage) {
        await postImage(initialImage);
      }
    },
    ...options,
    onMutate: async (variables, context) => {
      const { video, initialImage } = variables;

      await Promise.all([
        queryClient.cancelQueries({ queryKey: listQueryKey }),
        queryClient.cancelQueries({ queryKey: imageListQueryKey }),
        queryClient.cancelQueries({ queryKey: tagListQueryKey }),
      ]);

      // POSTed video replaces the video for the same room
      const prevVideo = queryClient
        .getQueryData<Video[]>(listQueryKey)
        ?.find(({ roomKey }) => roomKey === video.roomKey);

      // Remove previous and add new video
      queryClient.setQueryData<Video[]>(listQueryKey, (prevVideos) => {
        if (!prevVideos) return;
        return [...prevVideos.filter(({ key }) => key !== prevVideo?.key), video];
      });

      // Remove previous video images
      queryClient.setQueryData<SnapshotImage[]>(imageListQueryKey, (prevImages) => {
        if (!prevImages) return;

        const imagesToPreserve: SnapshotImage[] = [];
        const imagesToRemove: SnapshotImage[] = [];

        for (const image of prevImages) {
          if (image.videoKey === prevVideo?.key) {
            imagesToRemove.push(image);
          } else {
            imagesToPreserve.push(image);
          }
        }

        // Remove previous video items
        queryClient.setQueryData<Item[]>(tagListQueryKey, (prevTags) =>
          prevTags?.filter(({ imageKey }) => !imagesToRemove.some(({ key }) => key === imageKey))
        );

        if (initialImage) {
          imagesToPreserve.push(initialImage);
        }

        // Images without previous video images
        return imagesToPreserve;
      });

      options?.onMutate?.(variables, context);
    },
    onSettled: (response, error, variables, onMutateResult, context) => {
      invalidateIfAllMutationsDone(listQueryKey, [VIDEO_QUERY_KEY, jobKey]);

      // POST video often updates its job and room properties
      invalidateIfAllMutationsDone([JOB_QUERY_KEY, ENTITY_KEY, jobKey], mutationKey);
      invalidateIfAllMutationsDone([ROOM_QUERY_KEY, LIST_KEY, jobKey], mutationKey);

      // Invalidate affected entity lists
      invalidateIfAllMutationsDone(imageListQueryKey, mutationKey);
      invalidateIfAllMutationsDone(tagListQueryKey, mutationKey);

      options?.onSettled?.(response, error, variables, onMutateResult, context);
    },
  };
};

export const { useScopedMutation: usePostVideo, useScopedMutationBuilder: usePostVideoBuilder } =
  createScopedMutation(getPostVideoOptions);
